/**
 * ⟦selector-de-lotes⟧ `R4-UI0` · **EL DESPLEGABLE DE LOTES CON CASILLAS Y
 * BUSCADOR, EXTRAÍDO UNA VEZ.**
 *
 * R4-F2 regla 3 (PRD_R4.md:1386): «El filtro Lotes es un desplegable con
 * buscador (sin distinguir mayúsculas ni tildes) y una casilla por lote, del más
 * reciente al más antiguo y, a igual fecha de creación, por nombre, que se
 * cierra con Escape o tocando fuera…».
 *
 * ════════════════════════════════════════════════════════════════════════════
 * ⛔ LA PRIMITIVA NO ORDENA Y NO FIJA EL RÓTULO CERRADO.
 * ════════════════════════════════════════════════════════════════════════════
 *
 * · El orden de los lotes es el que trae el servidor (más reciente primero, y a
 *   igual fecha por nombre). Ordenar acá inventaría un criterio en el cliente y
 *   descartaría el del rango.
 * · El rótulo cerrado lo fija la pantalla y llega por parámetro: F2 dice el
 *   suyo, y Participantes sigue con el texto de sus CA de R3. Fijar uno adentro
 *   deja a la primitiva inservible para cualquier otra pantalla.
 *
 * ⚠️ Este componente vive en `src/selector-de-lotes.tsx`. Su `normalize` es de
 * NOMBRES DE LOTE: no debe quedar junto a ninguna mención de teléfono, porque
 * `padron/filtros.tsx:60-70` documenta un examen que lo busca cerca de esa
 * palabra.
 */

import { useEffect, useMemo, useRef, useState } from "react"

export interface LoteDelSelector {
  readonly id: string
  readonly nombre: string
}

/**
 * Pliega mayúsculas Y tildes para buscar: «nuñez» encuentra «Núñez».
 *
 * `normalize("NFD")` separa la letra de su diacrítico y el `replace` lo borra;
 * sin el `replace`, normalizar no alcanza para comparar «núñez» con «núñez».
 */
export function normalizarParaBuscar(texto: string): string {
  return texto
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
}

interface Props {
  readonly lotes: readonly LoteDelSelector[]
  readonly elegidos: readonly string[]
  /** El texto que se ve con el desplegable cerrado. Lo fija la pantalla. */
  readonly rotulo: string
  readonly onCambio: (ids: readonly string[]) => void
}

export function SelectorDeLotes({ lotes, elegidos, rotulo, onCambio }: Props) {
  const [abierto, setAbierto] = useState(false)
  const [busqueda, setBusqueda] = useState("")
  const contenedor = useRef<HTMLDivElement>(null)

  /*
   * Escape y toque fuera cierran el desplegable (PRD:1386). Los dos se registran
   * sólo mientras está abierto: un `document` escuchando siempre es un costo
   * que ninguna de las pantallas necesita.
   */
  useEffect(() => {
    if (!abierto) return
    const alTeclear = (evento: KeyboardEvent): void => {
      if (evento.key === "Escape") setAbierto(false)
    }
    const alTocarFuera = (evento: MouseEvent): void => {
      const destino = evento.target
      if (contenedor.current !== null && destino instanceof Node && !contenedor.current.contains(destino)) {
        setAbierto(false)
      }
    }
    document.addEventListener("keydown", alTeclear)
    document.addEventListener("mousedown", alTocarFuera)
    return () => {
      document.removeEventListener("keydown", alTeclear)
      document.removeEventListener("mousedown", alTocarFuera)
    }
  }, [abierto])

  const filtrados = useMemo(() => {
    const aguja = normalizarParaBuscar(busqueda)
    if (aguja === "") return lotes
    return lotes.filter((lote) => normalizarParaBuscar(lote.nombre).includes(aguja))
  }, [lotes, busqueda])

  const alternar = (id: string): void => {
    onCambio(elegidos.includes(id) ? elegidos.filter((x) => x !== id) : [...elegidos, id])
  }

  return (
    <div className="selector-lotes" ref={contenedor}>
      <button type="button" aria-expanded={abierto} onClick={() => setAbierto(!abierto)}>
        {rotulo}
      </button>
      {abierto && (
        <div className="selector-lotes__panel">
          <input
            type="search"
            value={busqueda}
            onChange={(evento) => setBusqueda(evento.target.value)}
            placeholder="Buscar lote"
          />
          <ul className="selector-lotes__lista">
            {/* R4-H-003 / R4-F2-CA-12: sin coincidencias, la lista lo dice. */}
            {filtrados.length === 0 && lotes.length > 0 && <li>No hay lotes con ese nombre.</li>}
            {filtrados.map((lote) => (
              <li key={lote.id}>
                <label>
                  <input
                    type="checkbox"
                    checked={elegidos.includes(lote.id)}
                    onChange={() => alternar(lote.id)}
                  />
                  {lote.nombre}
                </label>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}
